import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TemplateDataStore, TemplatesStore } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import {
  DialogComponent,
  DialogContentDirective,
  DialogTitleDirective,
} from '@qtek/shared/components';
import {
  DocumentTemplate,
  ITemplateData,
  TemplateTypes,
} from '@qtek/shared/models';
import { DEFAULT_QUILL_MODULES } from '@qtek/shared/utils';
import { QuillModule } from 'ngx-quill';
import { filter, take } from 'rxjs/operators';
import { adjustFoldersForPayload } from '../../utils/adjust-folder-for-payload';
import {
  FolderNode,
  FolderStructureBuilderComponent,
} from '../folder-structure-builder';
import { templateNameExistsValidator } from './template-name.validator';

export const createTemplateDialogConfig: MatDialogConfig = {
  width: 'min(1200px, 90vw)',
  height: 'min(700px, 90vh)',
  autoFocus: false,
};

export interface CreateTemplateDialogData {
  existingTemplates?: string[];
  templateType?: 'rtf' | 'docs';
}

@Component({
  selector: 'qt-create-template-dialog',
  templateUrl: './create-template-dialog.component.html',
  styleUrls: ['./create-template-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslationCoreModule,
    DialogComponent,
    DialogTitleDirective,
    DialogContentDirective,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    QuillModule,
    MatProgressSpinnerModule,
    FolderStructureBuilderComponent,
  ],
})
export class CreateTemplateDialogComponent implements OnInit, OnDestroy {
  private data = inject<CreateTemplateDialogData>(MAT_DIALOG_DATA);
  private templatesStore = inject(TemplatesStore);
  private templateDataStore = inject(TemplateDataStore);
  private dialogRef = inject(MatDialogRef<CreateTemplateDialogComponent>);
  private destroyRef = inject(DestroyRef);

  templateForm: FormGroup = new FormGroup({
    name: new FormControl<string>('', [
      Validators.required,
      templateNameExistsValidator(this.data.existingTemplates || []),
    ]),
    content: new FormControl<string>(''),
  });

  isCreating = false;
  createdTemplate: DocumentTemplate | null = null;
  quillModules = DEFAULT_QUILL_MODULES;
  folderStructure: FolderNode[] = [];

  get nameControl(): FormControl<string> {
    return this.templateForm.get('name') as FormControl<string>;
  }

  get contentControl(): FormControl<string> {
    return this.templateForm.get('content') as FormControl<string>;
  }

  get templateType(): string {
    return this.data.templateType || 'rtf';
  }

  onFolderStructureChanged(structure: FolderNode[]): void {
    this.folderStructure = structure;
    const filteredStructure = adjustFoldersForPayload(structure);
    this.templateForm.patchValue({
      content: JSON.stringify(filteredStructure),
    });
  }

  ngOnInit(): void {
    if (this.templateType === 'rtf') {
      this.contentControl.setValidators([Validators.required]);
    } else {
      this.contentControl.clearValidators();
    }
    this.contentControl.updateValueAndValidity();

    this.templatesStore.connect({ view: 'details' });
    this.createTemplateData();
  }

  ngOnDestroy(): void {
    this.templatesStore.disconnect();
  }

  createAndPublishTemplate(): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched();
      return;
    }

    this.isCreating = true;
    const formValue = this.templateForm.value;

    const templateType =
      this.templateType === 'rtf' ? TemplateTypes.RTF : TemplateTypes.FOLDER;

    this.templatesStore.createTemplate(formValue.name, templateType);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private createTemplateData() {
    this.templatesStore.templatesOps$
      .pipe(
        filter(
          ({ op, ok, res }) =>
            op === 'ins' &&
            ok &&
            (res as DocumentTemplate)?.name === this.nameControl.value
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ res }) => {
        const createdTemplate = res as DocumentTemplate;
        this.createdTemplate = createdTemplate;

        this.templateDataStore
          .createTemplateData(createdTemplate._id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: response => {
              if (!response.res) {
                this.isCreating = false;
                return;
              }
              this.publishTemplateData(createdTemplate, response.res);
            },
            error: () => {
              this.isCreating = false;
            },
          });
      });
  }

  private publishTemplateData(
    template: DocumentTemplate,
    tplData: ITemplateData
  ): void {
    const filteredStructure = adjustFoldersForPayload(this.folderStructure);
    const body =
      this.templateType === 'docs'
        ? { body: JSON.stringify(filteredStructure) }
        : { body: this.templateForm.get('content')?.value || '' };

    this.templateDataStore
      .updateTemplateData(tplData._id, tplData.version, body, 'PUBLISHED')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isCreating = false;
          this.dialogRef.close(template);
        },
        error: () => {
          this.isCreating = false;
        },
      });
  }
}
